// portals/shared/src/muiTheme.js
import { createTheme } from "@mui/material/styles";
import "@fontsource/lato";

export const preloadFonts = ["Lato"];

// One color per portal 
const BRAND_COLORS = {
  user: "#F59E0B",
  admin: "#F59E0B",
  subadmin: "#F59E0B",
  refinery: "#F59E0B",
};

export function getBaseTheme({ brand = "user", mode = "light" } = {}) {
  const brandPrimary = BRAND_COLORS[brand] || BRAND_COLORS.user;

  // ===== Build palette blocks shared by both modes =====
  const common = {
    info: { main: "#52C4E4" },
    controls: { main: "#34373E" },

    pdark: {
      light: "#125A6E",
      main: "#125A6E",
      dark: "#125A6E",
      contrastText: "#FFFFFF",
    },
    plight: {
      light: "#00A2FF3320",
      main: "#65CAE6",
      dark: "#65CAE6",
      contrastText: "#FFFFFF",
    },
    inactive: {
      main: "#D9D9D9",
      light: "#D9D9D9",
      dark: "#D9D9D9",
      contrastText: "#FFFFFF",
    },
    chipbg: {
      yellow1: "#F39E151A",
      errlight1: "#EC596233",
    },
    gray100: "#6B7280",
    gray150: "#6B7280",
    gray20: "#EAEAEA",
    success80: "#27AE60",
    success10: "#00A666",
    warning1: "#FDA240",
    dBlue: { main: "#566ED7", light: "#566ED7", dark: "#566ED7" },
    tertiaryBtn: { main: "#F2F3F5", contrastText: "#000" },
  };

  const light = {
    mode: "light",
    primary: {
      main: brandPrimary,
      white: "#fff",
      backgroundMain: '#fffdf8',
      background50: '#F59E0B30',
    },

    border: {
      thin: "#e0dddc",
    },

    secondary: { main: "#4D759B" },
    error: { main: "#EC5962" },
    warning: { main: "#FFA747" },
    success: { main: "#2fab64" },
    primary50: "#DFEBF7",
    primary100: "#BAD5ED",
    primary200: "#91BCE3",
    primary300: "#5899d5",
    primary400: "#3078bb",
    gray10: "#D1D5DB",
    gray30: "#9CA3Af",
    gray50: "#6B7280",
    gray70: "#4E535D",
    gray90: "#34373E",

    text: {
      primary: "#1c0e00",
      secondary: "#717182",
      disabled: "#D1D5DB",
      primaryChannel: "0 0 0",
      secondaryChannel: "0 0 0",
      black100: "#000000",
      mute: '#706b69',
      white: "#ffffff",
    },

    textSize: {
      xxs: '0.65rem',
      xs: '0.75rem',
      sm: '0.875rem',
      smd: '0.9rem',
      md: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.00rem',
      '5xl': '3rem',
      '6xl': '3.75rem',
      '7xl': '4.5rem',
      '8xl': '6rem',
      '9xl': '8rem',
    },

    divider: "#e5e7eb",

    background: {
      paper: "#ffffff",
      default: "#ffffff",
      light1: "#F6F6F6",
      light2: "#F5F5FA",
      light3: "#0083FF24",
      light4: "#F5F5F5",
      light5: "#E9F5F8",
      light6: "#F6F6F6",
      light7: "#F6F6F6",
      main: "#FFFFFF",
    },

    user: {
      grey: "#364153",
      grey50: "rgba(110, 121, 143, 1)",
      bgColor: "#FFFDF8",
    },

    chipBackground: {
      low: "#CCEDE0",
      mild: "#E9F5F8",
      medium: "#FFF2CC",
      high: "#FDE9EC",
      greylight: "#EFF3FD",
      default: "#CCEDE0",
    },
  };

  const dark = {
    mode: "dark",

    primary: {
      main: brandPrimary, // Keep your brand as-is
      white: "#ffffff",
      backgroundMain: "#0f0f0f",
      background50: "#F59E0B30", // can remain same if it's a translucent brand tint
    },

    border: {
      thin: "#2d2d2d",
    },

    secondary: { main: "#7CA4CC" },
    error: { main: "#FF6B6B" },
    warning: { main: "#FFB85C" },
    success: { main: "#3EDC81" },

    primary50: "#1A2A3A",
    primary100: "#23384C",
    primary200: "#2F4861",
    primary300: "#3C5D7D",
    primary400: "#4A74A0",

    gray10: "#2A2D33",
    gray30: "#3B3F46",
    gray50: "#4C5159",
    gray70: "#6A707A",
    gray90: "#B3B6BC",

    text: {
      primary: "#F5F5F5",
      secondary: "#B3B3C0",
      disabled: "#6B7280",
      primaryChannel: "255 255 255",
      secondaryChannel: "255 255 255",
      black100: "#ffffff",
      mute: "#A6A6A6",
      white: "#ffffff",
    },

    textSize: {
      xxs: "0.65rem",
      xs: "0.75rem",
      sm: "0.875rem",
      smd: "0.9rem",
      md: "1rem",
      lg: "1.125rem",
      xl: "1.25rem",
      "2xl": "1.5rem",
      "3xl": "1.875rem",
      "4xl": "2rem",
      "5xl": "3rem",
      "6xl": "3.75rem",
      "7xl": "4.5rem",
      "8xl": "6rem",
      "9xl": "8rem",
    },

    divider: "#2E2E2E",

    background: {
      paper: "#1A1A1A",
      default: "#121212",
      light1: "#1C1C1E",
      light2: "#1F1F23",
      light3: "#0083FF24",
      light4: "#1E1E1E",
      light5: "#12323A",
      light6: "#1C1C1C",
      light7: "#1C1C1C",
      main: "#121212",
    },

    user: {
      grey: "#A6B1C0",
      grey50: "rgba(150, 160, 180, 1)",
      bgColor: "#181818",
    },

    chipBackground: {
      low: "#1E3A2E",
      mild: "#1F2D31",
      medium: "#3A2F14",
      high: "#3A1F24",
      greylight: "#2A3244",
      default: "#1E3A2E",
    },
  };


  return createTheme({
    direction: "ltr",
    breakpoints: {
      values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1400 },
    },
    palette: {
      ...(mode === "light" ? light : dark),
      ...common,
    },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: [
        "ui-sans-serif",
        "system-ui",
        "-apple-system",
        "BlinkMacSystemFont",
        '"Segoe UI"',
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        '"Noto Sans"',
        "sans-serif",
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
        '"Noto Color Emoji"',
      ].join(","),
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 600,
      fontWeightBold: 700,

      h1: {
        fontWeight: 400, fontSize: 34, lineHeight: 1.24, letterSpacing: 0,
        "@media (max-width:1440px)": { fontSize: 26 },
        "@media (max-width:900px)": { fontSize: 18 },
      },
      h1b: {
        fontWeight: 700, fontSize: 34, lineHeight: 1.24, letterSpacing: 0.25,
        "@media (max-width:900px)": { fontSize: 32 },
      },
      h2: {
        fontWeight: 400, fontSize: 24, lineHeight: 1.333, letterSpacing: 0,
        "@media (max-width:1200px)": { fontSize: 18 },
        "@media (max-width:900px)": { fontSize: 14 },
      },
      h2bv: {
        fontWeight: 700, fontSize: 26, lineHeight: 1.333, letterSpacing: 0,
        "@media (max-width:1440px)": { fontSize: 20 },
        "@media (max-width:900px)": { fontSize: 18 },
      },
      h2b: { fontWeight: 700, fontSize: 26, lineHeight: 1.333, letterSpacing: 0 },
      h3: { fontWeight: 400, fontSize: 20, lineHeight: 1.333, letterSpacing: 0 },
      h3v: {
        fontWeight: 400, fontSize: 20, lineHeight: 1.333, letterSpacing: 0,
        "@media (max-width:1440px)": { fontSize: 18 },
        "@media (max-width:900px)": { fontSize: 16 },
      },
      h3b: { fontWeight: 700, fontSize: 20, lineHeight: 1.333, letterSpacing: 0 },
      h4: { fontWeight: 400, fontSize: 18, lineHeight: 1.333, letterSpacing: 0 },
      h4b: { fontWeight: 700, fontSize: 18, lineHeight: 1.333, letterSpacing: 0 },
      h5: { fontWeight: 400, fontSize: 18, lineHeight: 1.333, letterSpacing: 0 },
      h6: { fontWeight: 400, fontSize: 18, lineHeight: 1.333, letterSpacing: 0 },

      body1: { fontWeight: 400, fontSize: 16, lineHeight: 1.5, letterSpacing: 0 },
      body1bv: {
        fontWeight: 700, fontSize: 16, lineHeight: 1.5, letterSpacing: 0,
        "@media (max-width:1440px)": { fontSize: 14 },
        "@media (max-width:900px)": { fontSize: 12 },
      },
      body1b: { fontWeight: 700, fontSize: 16, lineHeight: 1.5, letterSpacing: 0 },
      body1wt: { fontWeight: 700, fontSize: 16, lineHeight: 1.5, letterSpacing: 0 },

      body2: { fontWeight: 400, fontSize: 16, lineHeight: 1.4, letterSpacing: 0 },
      body2b: { fontWeight: 700, fontSize: 14, lineHeight: 1.4, letterSpacing: 0 },

      subtitle1: { fontWeight: 400, fontSize: 16, lineHeight: 1.2, letterSpacing: 0 },
      subtitle1v: {
        fontWeight: 400, fontSize: 12, lineHeight: 1.2, letterSpacing: 0,
        "@media (max-width:900px)": { fontSize: 10 },
      },
      subtitle1b: { fontWeight: 700, fontSize: 12, lineHeight: 1.2, letterSpacing: 0 },

      caption: { fontWeight: 600, fontSize: 14, letterSpacing: 0 },
      button: { fontWeight: 400, fontSize: 14, letterSpacing: 0 },
    },

    components: {
      MuiAutocomplete: {
        styleOverrides: { popper: { fontSize: "14px" }, option: { fontSize: "14px" } },
      },
      MuiButtonBase: {
        styleOverrides: { root: { fontFamily: preloadFonts.join(",") } },
      },
      MuiButton: {
        defaultProps: { variant: "outlined" },
        styleOverrides: {
          sizeSmall: { height: "32px" },
          root: { textTransform: "none", borderRadius: 8, fontSize: "14px", height: "40px" },
        },
      },
      MuiIconButton: {
        styleOverrides: { root: { "&:hover": { backgroundColor: "#E9F5F8" } } },
      },
      MuiCard: { styleOverrides: { root: { borderRadius: 16 } } },
      MuiDialogActions: { styleOverrides: { root: { paddingLeft: "24px", paddingRight: "24px" } } },
      MuiInputBase: { styleOverrides: { sizeSmall: { height: "40px" } } },
      MuiMenuItem: { styleOverrides: { root: { "&:hover": { backgroundColor: "#E9F5F8" }, fontSize: "14px" } } },
      MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 8, fontSize: "14px" } } },
      MuiTab: { styleOverrides: { root: { textTransform: "none" } } },
      MuiTextField: { defaultProps: { margin: "dense" } },
      MuiTooltip: { styleOverrides: { tooltip: { fontSize: "14px", fontFamily: preloadFonts.join(",") } } },
      MuiCheckbox: { styleOverrides: { root: { color: "#EAEAEA" } } },
      MuiFormControlLabel: { styleOverrides: { label: { fontSize: "14px" } } },
      MuiFormLabel: { styleOverrides: { root: { fontSize: "14px", "&.Mui-checked": { color: "#215382" } } } },
    },
  });
}
